/**
 * TaskGroupHeroImage Component
 *
 * Feature: 650-task-grouping
 *
 * Renders the hero image for a task group, designed to span
 * the full height of the collapsed group state.
 */

import { AdaptivePhoto, type ImageVariant } from '@/components/photos/AdaptivePhoto';

export interface TaskGroupHeroImageProps {
  heroPhoto: { id: string; variants: ImageVariant[] } | null;
  sessionTitle: string | null;
  isLoading: boolean;
}

export function TaskGroupHeroImage({
  heroPhoto,
  sessionTitle,
  isLoading,
}: TaskGroupHeroImageProps) {
  return (
    <div className="w-40 flex-shrink-0 bg-surface-2 relative self-stretch">
      {isLoading ? (
        <div className="absolute inset-0 flex items-center justify-center" role="status" aria-live="polite">
          <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <span className="sr-only">Loading hero image</span>
        </div>
      ) : heroPhoto ? (
        <AdaptivePhoto
          photo={heroPhoto}
          alt={sessionTitle || 'Critique session'}
          className="absolute inset-0"
          imgClassName="w-full h-full object-cover"
        />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center text-muted">
          <svg className="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        </div>
      )}
      {/* Critique badge overlay */}
      <div className="absolute bottom-2 right-2 w-6 h-6 bg-primary rounded-full flex items-center justify-center border-2 border-surface-1 shadow-sm">
        <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
        </svg>
      </div>
    </div>
  );
}
